import { useState, useRef, useCallback } from 'react';

/**
 * Custom hook for managing assessment progress animations
 * Handles animation keys for categories and overall progress to trigger smooth transitions
 */
const useAssessmentAnimations = () => {
  const hasAnimatedRef = useRef(false);
  const [categoryAnimationKeys, setCategoryAnimationKeys] = useState({});
  const [overallProgressAnimationKey, setOverallProgressAnimationKey] = useState(0);

  /**
   * Triggers animations for all categories and overall progress
   * Used on initial load or when loading historical assessments
   * @param {Object} categoryScores - Object containing category scores
   * @param {number} delay - Delay in milliseconds before triggering animation (default: 50ms)
   */
  const triggerAllAnimations = useCallback((categoryScores, delay = 50) => {
    setTimeout(() => {
      // Initialize all category animation keys
      const initialKeys = {};
      Object.keys(categoryScores || {}).forEach(catKey => {
        initialKeys[catKey] = 1;
      });
      setCategoryAnimationKeys(initialKeys);
      setOverallProgressAnimationKey(prev => prev + 1);
    }, delay);
  }, []);

  /**
   * Triggers animation for all categories and overall progress on initial load
   * Only triggers once per session using hasAnimatedRef
   * @param {Object} categoryScores - Object containing category scores
   */
  const triggerInitialAnimations = useCallback((categoryScores) => {
    if (!hasAnimatedRef.current) {
      triggerAllAnimations(categoryScores);
      hasAnimatedRef.current = true;
    }
  }, [triggerAllAnimations]);

  /**
   * Triggers animation for a specific category
   * Used when expanding/collapsing categories or updating individual category data
   * @param {string} categoryKey - The category key to animate
   * @param {number} delay - Delay in milliseconds before triggering animation (default: 100ms)
   */
  const triggerCategoryAnimation = useCallback((categoryKey, delay = 100) => {
    setTimeout(() => {
      setCategoryAnimationKeys(prev => ({
        ...prev,
        [categoryKey]: (prev[categoryKey] || 0) + 1
      }));
    }, delay);
  }, []);

  /**
   * Resets the hasAnimated flag, allowing initial animations to run again
   * Useful when navigating back from historical view or resetting the component
   */
  const resetAnimations = useCallback(() => {
    hasAnimatedRef.current = false;
  }, []);

  /**
   * Gets the animation key for a specific category
   * @param {string} categoryKey - The category key
   * @returns {number} Animation key for the category
   */
  const getCategoryAnimationKey = useCallback((categoryKey) => {
    return categoryAnimationKeys[categoryKey] || 0;
  }, [categoryAnimationKeys]);

  /**
   * Checks if initial animations have been triggered
   * @returns {boolean} True if animations have run
   */
  const hasAnimated = useCallback(() => {
    return hasAnimatedRef.current;
  }, []);

  return {
    // Animation keys
    categoryAnimationKeys,
    overallProgressAnimationKey,

    // Animation triggers
    triggerAllAnimations,
    triggerInitialAnimations,
    triggerCategoryAnimation,

    // Utility functions
    resetAnimations,
    getCategoryAnimationKey,
    hasAnimated
  };
};

export default useAssessmentAnimations;
